import React from 'react'
import {type GestureResponderEvent, View} from 'react-native'
import Animated from 'react-native-reanimated'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import {msg, plural, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigationState} from '@react-navigation/native'

import {APP_DISPLAY_NAME} from '#/lib/constants'
import {useHideBottomBarBorder} from '#/lib/hooks/useHideBottomBarBorder'
import {useMinimalShellFooterTransform} from '#/lib/hooks/useMinimalShellTransform'
import {useOpenComposer} from '#/lib/hooks/useOpenComposer'
import {getCurrentRoute, isTab} from '#/lib/routes/helpers'
import {makeProfileLink} from '#/lib/routes/links'
import {type CommonNavigatorParams} from '#/lib/routes/types'
import {useGate} from '#/lib/statsig/statsig'
import {useIsActivationComplete} from '#/state/activation'
import {useHomeBadge} from '#/state/home-badge'
import {useUnreadMessageCount} from '#/state/queries/messages/list-conversations'
import {useSession} from '#/state/session'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {useShellLayout} from '#/state/shell/shell-layout'
import {useCloseAllActiveElements} from '#/state/util'
import {Link} from '#/view/com/util/Link'
import {Logo} from '#/view/icons/Logo'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {
  HomeOpen_Filled_Corner0_Rounded as HomeFilled,
  HomeOpen_Stoke2_Corner0_Rounded as Home,
} from '#/components/icons/HomeOpen'
import {MagnifyingGlass_Filled_Stroke2_Corner0_Rounded as MagnifyingGlassFilled} from '#/components/icons/MagnifyingGlass'
import {MagnifyingGlass2_Stroke2_Corner0_Rounded as MagnifyingGlass} from '#/components/icons/MagnifyingGlass2'
import {
  Message_Stroke2_Corner0_Rounded as Message,
  Message_Stroke2_Corner0_Rounded_Filled as MessageFilled,
} from '#/components/icons/Message'
import {PlusLarge_Stroke2_Corner0_Rounded as PlusIcon} from '#/components/icons/Plus'
import {
  UserCircle_Filled_Corner0_Rounded as UserCircleFilled,
  UserCircle_Stroke2_Corner0_Rounded as UserCircle,
} from '#/components/icons/UserCircle'
import {Text} from '#/components/Typography'
import {styles} from './BottomBarStyles'
import {FloatingTabBar, type FloatingTabBarItemBase} from './FloatingTabBar'

type FloatingItem = FloatingTabBarItemBase & {
  routeName: string
  href: string
  renderIcon: (color: string, isActive: boolean) => JSX.Element
  testID?: string
  accessibilityLabel?: string
  accessibilityHint?: string
  notificationCount?: string
  hasNew?: boolean
  navigationAction?: 'push' | 'replace' | 'navigate'
  onPress?: (e: GestureResponderEvent) => void | false
  variant?: 'default' | 'compose'
}

const DEBUG_TAB_BOUNDS = __DEV__ && false
const TABBAR_DEBUG_FLAG = 'tellus_debug_tabbar'
const isDev =
  typeof __DEV__ !== 'undefined'
    ? __DEV__
    : process.env.NODE_ENV !== 'production'

function isTabBarDebugEnabled() {
  if (!isDev || typeof window === 'undefined' || !window.localStorage)
    return false
  try {
    return window.localStorage.getItem(TABBAR_DEBUG_FLAG) === '1'
  } catch {
    return false
  }
}

function tabBarDebug(event: string, payload?: unknown) {
  if (!isTabBarDebugEnabled()) return
  if (payload !== undefined) {
    console.debug(`[BottomBarWeb] ${event}`, payload)
  } else {
    console.debug(`[BottomBarWeb] ${event}`)
  }
}

export function BottomBarWeb() {
  const {_} = useLingui()
  const {hasSession, currentAccount} = useSession()
  const t = useTheme()
  const safeAreaInsets = useSafeAreaInsets()
  const {requestSwitchToAccount} = useLoggedOutViewControls()
  const closeAllActiveElements = useCloseAllActiveElements()
  const {footerHeight} = useShellLayout()
  const hideBorder = useHideBottomBarBorder()
  const footerMinimalShellTransform = useMinimalShellFooterTransform()
  const {openComposer} = useOpenComposer()
  const iconSize = 24
  const isDark = t.scheme === 'dark'
  const bottomOffset = 12 + safeAreaInsets.bottom + 8
  const pillBackground = isDark
    ? 'rgba(20,20,22,0.55)'
    : 'rgba(245,245,248,0.65)'
  const iconColorActive = isDark ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.85)'
  const iconColorInactive = isDark
    ? 'rgba(255,255,255,0.75)'
    : 'rgba(0,0,0,0.55)'
  const highlightBackground = isDark
    ? 'rgba(255,255,255,0.14)'
    : 'rgba(0,0,0,0.08)'
  const highlightBorderColor = 'rgba(255,255,255,0.10)'
  const composePillBackground = isDark
    ? 'rgba(84,154,255,0.26)'
    : 'rgba(10,88,255,0.14)'
  const composePillBorderColor = isDark
    ? 'rgba(175,210,255,0.42)'
    : 'rgba(10,88,255,0.30)'
  const composeIconColor = isDark ? 'rgba(255,255,255,0.98)' : '#1f4fff'

  const unreadMessageCount = useUnreadMessageCount()
  const hasHomeBadge = useHomeBadge()
  const gate = useGate()
  const isActivationComplete = useIsActivationComplete()
  const lockToActivate = hasSession && !isActivationComplete
  const currentRoute = useNavigationState(state => {
    if (!state) {
      return {name: 'Home'}
    }
    return getCurrentRoute(state)
  })
  const profileName =
    currentRoute.name === 'Profile'
      ? (currentRoute.params as CommonNavigatorParams['Profile'] | undefined)
          ?.name
      : undefined
  const isActiveRoute = React.useCallback(
    (routeName: string) => {
      if (currentRoute.name === 'Profile') {
        return (
          isTab(currentRoute.name, routeName) &&
          profileName ===
            (routeName === 'Profile' ? currentAccount?.handle : profileName)
        )
      }
      return isTab(currentRoute.name, routeName)
    },
    [currentRoute.name, currentAccount?.handle, profileName],
  )

  const showSignIn = React.useCallback(() => {
    closeAllActiveElements()
    requestSwitchToAccount({requestedAccount: 'none'})
  }, [requestSwitchToAccount, closeAllActiveElements])

  const showCreateAccount = React.useCallback(() => {
    closeAllActiveElements()
    requestSwitchToAccount({requestedAccount: 'new'})
    // setShowLoggedOut(true)
  }, [requestSwitchToAccount, closeAllActiveElements])

  const onPressCompose = React.useCallback(
    (e: GestureResponderEvent) => {
      // Keep compose in-tab-bar on web mobile while preventing link navigation.
      e.preventDefault()
      openComposer({})
      return false
    },
    [openComposer],
  )

  // iOS Safari/PWA can briefly report incomplete activation state; keep the bar
  // visible and let lockToActivate route taps to /activate instead of hard-hiding it.
  React.useEffect(() => {
    tabBarDebug('mount/state', {
      hasSession,
      isActivationComplete,
      lockToActivate,
      routeName: currentRoute.name,
    })
  }, [hasSession, isActivationComplete, lockToActivate, currentRoute.name])

  const items: FloatingItem[] = [
    {
      key: 'Home',
      routeName: lockToActivate ? 'Activate' : 'Home',
      href: lockToActivate ? '/activate' : '/',
      hasNew: hasHomeBadge && gate('remove_show_latest_button'),
      accessibilityLabel: _(msg`Home`),
      renderIcon: (color, isActive) =>
        isActive ? (
          <HomeFilled
            aria-hidden={true}
            width={iconSize}
            style={[styles.ctrlIcon, {color}, styles.homeIcon]}
          />
        ) : (
          <Home
            aria-hidden={true}
            width={iconSize}
            style={[styles.ctrlIcon, {color}, styles.homeIcon]}
          />
        ),
      isActive: isActiveRoute('Home'),
    },
    {
      key: 'Search',
      routeName: lockToActivate ? 'Activate' : 'Search',
      href: lockToActivate ? '/activate' : '/search',
      accessibilityLabel: _(msg`Search`),
      renderIcon: (color, isActive) =>
        isActive ? (
          <MagnifyingGlassFilled
            aria-hidden={true}
            width={iconSize}
            style={[styles.ctrlIcon, {color}, styles.searchIcon]}
          />
        ) : (
          <MagnifyingGlass
            aria-hidden={true}
            width={iconSize}
            style={[styles.ctrlIcon, {color}, styles.searchIcon]}
          />
        ),
      isActive: isActiveRoute('Search'),
    },
    {
      key: 'Compose',
      routeName: lockToActivate ? 'Activate' : 'Compose',
      href: lockToActivate ? '/activate' : '/',
      accessibilityLabel: _(msg`New post`),
      renderIcon: () => (
        <PlusIcon
          aria-hidden={true}
          width={20}
          style={[styles.ctrlIcon, {color: composeIconColor}]}
        />
      ),
      isActive: false,
      onPress: lockToActivate ? undefined : onPressCompose,
      variant: 'compose',
    },
    {
      key: 'Messages',
      routeName: lockToActivate ? 'Activate' : 'Messages',
      href: lockToActivate ? '/activate' : '/chat',
      notificationCount: unreadMessageCount.numUnread,
      hasNew: unreadMessageCount.hasNew,
      accessibilityLabel: _(msg`Tellus Chat`),
      accessibilityHint:
        unreadMessageCount.count > 0
          ? _(
              msg`${plural(unreadMessageCount.numUnread ?? 0, {
                one: '# unread item',
                other: '# unread items',
              })}` || '',
            )
          : '',
      renderIcon: (color, isActive) =>
        isActive ? (
          <MessageFilled
            aria-hidden={true}
            width={iconSize}
            style={[styles.ctrlIcon, {color}, styles.messagesIcon]}
          />
        ) : (
          <Message
            aria-hidden={true}
            width={iconSize}
            style={[styles.ctrlIcon, {color}, styles.messagesIcon]}
          />
        ),
      isActive: isActiveRoute('Messages'),
    },
    {
      key: 'Profile',
      routeName: lockToActivate ? 'Activate' : 'Profile',
      href: lockToActivate
        ? '/activate'
        : currentAccount
          ? makeProfileLink({
              did: currentAccount.did,
              handle: currentAccount.handle,
            })
          : '/',
      accessibilityLabel: _(msg`Profile`),
      renderIcon: (color, isActive) =>
        isActive ? (
          <UserCircleFilled
            aria-hidden={true}
            width={iconSize}
            style={[styles.ctrlIcon, {color}, styles.profileIcon]}
          />
        ) : (
          <UserCircle
            aria-hidden={true}
            width={iconSize}
            style={[styles.ctrlIcon, {color}, styles.profileIcon]}
          />
        ),
      isActive: isActiveRoute('Profile'),
      navigationAction:
        currentRoute.name === 'Profile' &&
        profileName !== currentAccount?.handle
          ? 'push'
          : 'navigate',
    },
  ]

  return hasSession ? (
    <FloatingTabBar<FloatingItem>
      items={items}
      bottomOffset={bottomOffset}
      webBottomOffset="calc(env(safe-area-inset-bottom) + 12px + 8px)"
      containerProps={{role: 'navigation', pointerEvents: 'auto'}}
      containerStyle={[footerMinimalShellTransform, {zIndex: 140}]}
      onPillLayout={height => {
        if (typeof document !== 'undefined') {
          const tabbarHeight = Math.max(
            0,
            Math.round(height + safeAreaInsets.bottom),
          )
          document.documentElement.style.setProperty(
            '--tabbar-h',
            `${tabbarHeight}px`,
          )
        }
        tabBarDebug('pill layout', {
          height,
          bottomOffset,
          totalFooterHeight: height + bottomOffset,
        })
        footerHeight.set(height + bottomOffset)
      }}
      pillStyle={{
        backgroundColor: pillBackground,
        borderColor: hideBorder ? 'transparent' : 'rgba(255,255,255,0.10)',
      }}
      highlightStyle={{
        backgroundColor: highlightBackground,
        borderColor: highlightBorderColor,
      }}
      debug={DEBUG_TAB_BOUNDS}
      renderItem={({item, onLayout, isActive}) => (
        <Link
          href={item.href}
          style={[
            styles.ctrl,
            item.variant === 'compose' && styles.ctrlCompose,
          ]}
          onLayout={onLayout}
          navigationAction={item.navigationAction}
          {...(item.onPress ? {onPress: item.onPress} : {})}
          aria-role="link"
          aria-label={item.accessibilityLabel ?? item.routeName}
          accessible={true}
          accessibilityLabel={item.accessibilityLabel}
          accessibilityHint={item.accessibilityHint}>
          {item.variant === 'compose' ? (
            <View
              style={[
                styles.composePill,
                {
                  backgroundColor: composePillBackground,
                  borderColor: composePillBorderColor,
                },
                isActive && styles.composePillActive,
              ]}>
              {item.renderIcon(composeIconColor, isActive)}
            </View>
          ) : (
            item.renderIcon(
              isActive ? iconColorActive : iconColorInactive,
              isActive,
            )
          )}
          {item.notificationCount ? (
            <View
              style={[styles.notificationCount, styles.notificationCountWeb]}
              aria-label={_(
                msg`${plural(item.notificationCount, {
                  one: '# unread item',
                  other: '# unread items',
                })}`,
              )}>
              <Text style={styles.notificationCountLabel}>
                {item.notificationCount}
              </Text>
            </View>
          ) : item.hasNew ? (
            <View style={styles.hasNewBadge} />
          ) : null}
        </Link>
      )}
    />
  ) : (
    <Animated.View
      role="navigation"
      style={[
        styles.bottomBarLegacy,
        styles.bottomBarLegacyWeb,
        t.atoms.bg,
        {paddingBottom: safeAreaInsets.bottom},
        footerMinimalShellTransform,
        hideBorder
          ? {borderColor: t.atoms.bg.backgroundColor}
          : t.atoms.border_contrast_low,
      ]}
      onLayout={event => {
        const height = event.nativeEvent.layout.height
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty(
            '--tabbar-h',
            `${Math.round(height)}px`,
          )
        }
        footerHeight.set(height)
      }}>
      <View
        style={{
          width: '100%',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 14,
          paddingBottom: 14,
          paddingLeft: 14,
          paddingRight: 6,
          gap: 8,
        }}>
        <View style={{flexDirection: 'row', alignItems: 'center', gap: 12}}>
          <Logo width={32} />
          <View style={{paddingTop: 4}}>
            <Text style={[a.text_lg, a.font_heavy, t.atoms.text]}>
              {APP_DISPLAY_NAME}
            </Text>
          </View>
        </View>

        <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
          <Button
            onPress={showCreateAccount}
            label={_(msg`Create account`)}
            size="small"
            variant="solid"
            color="primary">
            <ButtonText>
              <Trans>Create account</Trans>
            </ButtonText>
          </Button>
          <Button
            onPress={showSignIn}
            label={_(msg`Sign in`)}
            size="small"
            variant="solid"
            color="secondary">
            <ButtonText>
              <Trans>Sign in</Trans>
            </ButtonText>
          </Button>
        </View>
      </View>
    </Animated.View>
  )
}
